import React, { useEffect } from "react";
import { Typography, Card, Space, Row, Col } from "antd";
import {
  FileTextOutlined,
  LockOutlined,
  EyeOutlined,
  UserOutlined,
  GlobalOutlined,
  MailOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../Home/LanguageContext";
import { PublicPageLayout } from "../../Layout/PublicPageLayout";

const { Title, Paragraph } = Typography;

const PrivacyPageContent: React.FC = () => {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = "Privacy Policy - Lingua Talk";
  }, []);

  return (
  <>
    <Helmet>
      <title>Privacy Policy — LinguaTalk</title>
      <meta name="description" content="LinguaTalk privacy policy. How we collect, use, and protect your data." />
    </Helmet>
    <main className="public-main">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <header className="public-hero public-hero-purple">
          <FileTextOutlined className="public-hero-icon" />
          <h1 className="public-hero-title">{t("privacyTitle")}</h1>
          <p className="public-hero-desc">{t("privacyIntro")}</p>
        </header>

        <Card
          className="public-card"
          title={
            <Space>
              <EyeOutlined />
              <span>{t("informationWeCollect")}</span>
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Title level={4}>{t("accountInformationTitle")}</Title>
              <ul className="public-list">
                <li>{t("infoCollectEmailUsername")}</li>
                <li>{t("infoCollectProfileInfo")}</li>
                <li>{t("infoCollectLanguagePrefs")}</li>
                <li>{t("infoCollectSettingsPrefs")}</li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={4}>{t("usageDataTitle")}</Title>
              <ul className="public-list">
                <li>{t("usageDataConversationsAI")}</li>
                <li>{t("usageDataMessagesPartners")}</li>
                <li>{t("usageDataProgressStats")}</li>
                <li>{t("usageDataAppPatterns")}</li>
              </ul>
            </Col>
          </Row>
        </Card>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card
              className="public-card"
              title={
                <Space>
                  <UserOutlined />
                  <span>{t("howWeUseTitle")}</span>
                </Space>
              }
            >
              <ul className="public-list">
                <li><strong>{t("provideServicesTitle")}:</strong> {t("provideServicesDesc")}</li>
                <li><strong>{t("improveExperienceTitle")}:</strong> {t("improveExperienceDesc")}</li>
                <li><strong>{t("safetyModerationTitle")}:</strong> {t("safetyModerationDesc")}</li>
                <li><strong>{t("communicationTitle")}:</strong> {t("communicationDesc")}</li>
                <li><strong>{t("analyticsTitle")}:</strong> {t("analyticsDesc")}</li>
              </ul>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              className="public-card"
              title={
                <Space>
                  <LockOutlined />
                  <span>{t("dataProtectionTitle")}</span>
                </Space>
              }
            >
              <ul className="public-list">
                <li><strong>{t("encryptionTitle")}:</strong> {t("encryptionDesc")}</li>
                <li><strong>{t("accessControlsTitle")}:</strong> {t("accessControlsDesc")}</li>
                <li><strong>{t("regularAuditsTitle")}:</strong> {t("regularAuditsDesc")}</li>
                <li><strong>{t("secureInfrastructureTitle")}:</strong> {t("secureInfrastructureDesc")}</li>
                <li><strong>{t("dataMinimizationTitle")}:</strong> {t("dataMinimizationDesc")}</li>
              </ul>
            </Card>
          </Col>
        </Row>

        <Card
          className="public-card"
          title={
            <Space>
              <GlobalOutlined />
              <span>{t("dataSharingTitle")}</span>
            </Space>
          }
        >
          <Paragraph><strong>{t("noSellData")}</strong></Paragraph>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <div className="public-stat-block">
                <Title level={4}>{t("serviceProvidersTitle")}</Title>
                <Paragraph>{t("serviceProvidersDesc")}</Paragraph>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="public-stat-block">
                <Title level={4}>{t("legalRequirementsTitle")}</Title>
                <Paragraph>{t("legalRequirementsDesc")}</Paragraph>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div className="public-stat-block">
                <Title level={4}>{t("withConsentTitle")}</Title>
                <Paragraph>{t("withConsentDesc")}</Paragraph>
              </div>
            </Col>
          </Row>
        </Card>

        <Card
          className="public-card"
          title={
            <Space>
              <UserOutlined />
              <span>{t("yourRightsTitle")}</span>
            </Space>
          }
        >
          <Row gutter={[16, 16]}>
            {[
              [t("rightAccessTitle"), t("rightAccessDesc")],
              [t("rightCorrectionTitle"), t("rightCorrectionDesc")],
              [t("rightDeletionTitle"), t("rightDeletionDesc")],
              [t("rightPortabilityTitle"), t("rightPortabilityDesc")],
            ].map(([title, desc]) => (
              <Col key={title} xs={24} sm={12} md={6}>
                <div className="public-stat-block">
                  <Title level={4}>{title}</Title>
                  <Paragraph>{desc}</Paragraph>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card
              className="public-card public-card-warm"
              title={
                <Space>
                  <MailOutlined />
                  <span>{t("contactUsTitle")}</span>
                </Space>
              }
            >
              <div className="public-center">
                <Paragraph>{t("contactUsDesc")}</Paragraph>
                <Paragraph>
                  <a className="public-link" href="mailto:support@linguatalk.com">
                    support@linguatalk.com
                  </a>
                </Paragraph>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              className="public-card public-card-highlight"
              title={
                <Space>
                  <ClockCircleOutlined />
                  <span>{t("policyUpdatesTitle")}</span>
                </Space>
              }
            >
              <div className="public-center">
                <Paragraph>{t("policyUpdatesDesc")}</Paragraph>
                <Paragraph><strong>{t("lastUpdated")}</strong></Paragraph>
              </div>
            </Card>
          </Col>
        </Row>
      </Space>
    </main>
  </>
  );
};

const PrivacyPage: React.FC = () => (
  <PublicPageLayout>
    <PrivacyPageContent />
  </PublicPageLayout>
);

export default PrivacyPage;
